import { useSubRouteParams } from '@data/hooks/use-sub-route-params/use-sub-route-params';
import { useGetMeApiWithSessionContext } from 'eds-common/api/v2';
import { useGetClassesApi, type Class } from 'eds-common/api/v3';
import { createContext, useContext, useMemo } from 'react';

interface StoryFilterContextProps {
  classId?: string;
  orgId?: string;
  teacherId?: string;
  familyId?: string;
  contentId?: string;
  quizId?: string;
  exitTicketId?: string;
  shortWriteId?: string;
  selectedStudentId?: string;
  classes?: Class[];
  isLoading: boolean;
  isError: boolean;
  isStandalone?: boolean;
}

const defaultContext: StoryFilterContextProps = {
  classes: [],
  isLoading: false,
  isError: false,
};

const StoryFilterContext =
  createContext<StoryFilterContextProps>(defaultContext);

export interface StoryFilterProviderProps {
  familyId?: string;
  contentId?: string;
  quizId?: string;
  exitTicketId?: string;
  shortWriteId?: string;
  isStandalone?: boolean;
  children: JSX.Element | JSX.Element[];
}

export function StoryFilterProvider({
  familyId,
  contentId,
  quizId,
  exitTicketId,
  shortWriteId,
  isStandalone,
  children,
}: StoryFilterProviderProps) {
  const {
    data: user,
    isLoading: userLoading,
    isError: userError,
  } = useGetMeApiWithSessionContext();
  const {
    data: classes,
    isLoading: classesLoading,
    isError: classesError,
  } = useGetClassesApi({
    userId: user?.userContext.userId ?? '',
    orgId: user?.userContext.currentOrg.orgId ?? '',
    source: user?.userContext.userSource ?? 'MyScholastic',
  });

  // Sort classes alphabetically by name
  const sortedClasses = useMemo(() => {
    return classes
      ? [...classes].sort((a, b) => a.name.localeCompare(b.name))
      : [];
  }, [classes]);

  // Set default class ID to the first class in the sorted list
  const defaultClassId = useMemo(
    () => sortedClasses[0]?.class_id ?? undefined,
    [sortedClasses]
  );

  const { classId: routeClassId } = useSubRouteParams(
    'work/:page/class/:classId'
  );

  // get classId again since this hook doesn't dynamically match subroutes
  // the way it probably should at the moment
  const { studentId: selectedStudentId, classId: routeStudentClassId } =
    useSubRouteParams('work/:page/class/:classId/student/:studentId');

  const value = useMemo<StoryFilterContextProps>(
    () => ({
      classId: routeClassId ?? routeStudentClassId ?? defaultClassId,
      teacherId: user?.userContext.userId,
      orgId: user?.userContext.currentOrg.orgId,
      familyId,
      contentId,
      quizId,
      exitTicketId,
      shortWriteId,
      selectedStudentId,
      classes: sortedClasses,
      isLoading: userLoading || classesLoading,
      isError: userError || classesError,
      isStandalone,
    }),
    [
      routeClassId,
      routeStudentClassId,
      defaultClassId,
      user?.userContext.userId,
      user?.userContext.currentOrg.orgId,
      familyId,
      contentId,
      quizId,
      exitTicketId,
      shortWriteId,
      selectedStudentId,
      sortedClasses,
      userLoading,
      classesLoading,
      userError,
      classesError,
      isStandalone,
    ]
  );

  return (
    <StoryFilterContext.Provider value={value}>
      {children}
    </StoryFilterContext.Provider>
  );
}

export function useClassId() {
  const { classId } = useContext(StoryFilterContext);

  return classId;
}

export function useOrgId() {
  const { orgId } = useContext(StoryFilterContext);

  return orgId as string;
}

export function useTeacherId() {
  const { teacherId } = useContext(StoryFilterContext);

  return teacherId as string;
}

export function useFamilyId() {
  const { familyId } = useContext(StoryFilterContext);

  return familyId as string;
}

export function useContentId() {
  const { contentId } = useContext(StoryFilterContext);

  return contentId as string;
}

export function useQuizId() {
  const { quizId } = useContext(StoryFilterContext);

  return quizId as string;
}

export function useExitTicketId() {
  const { exitTicketId } = useContext(StoryFilterContext);

  return exitTicketId as string;
}

export function useShortWriteId() {
  const { shortWriteId } = useContext(StoryFilterContext);

  return shortWriteId as string;
}

export function useClasses() {
  const { classes } = useContext(StoryFilterContext);

  return classes as Class[];
}

export function useSelectedStudentId() {
  const { selectedStudentId } = useContext(StoryFilterContext);

  return selectedStudentId;
}

export function useIsLoading() {
  const { isLoading } = useContext(StoryFilterContext);

  return isLoading;
}

export function useIsError() {
  const { isError } = useContext(StoryFilterContext);

  return isError;
}

export function useCurrentClass() {
  const { classId, classes } = useContext(StoryFilterContext);

  return useMemo(
    () => classes?.find((cls) => cls.class_id === classId),
    [classId, classes]
  );
}

export function useIsStandalone() {
  const { isStandalone } = useContext(StoryFilterContext);

  return isStandalone;
}

export function useStudentWorkLink() {
  const { quizId, exitTicketId, shortWriteId, classId } =
    useContext(StoryFilterContext);

  if (quizId) {
    return `work/quiz/class/${classId}`;
  }

  if (exitTicketId) {
    return `work/exit-tickets/class/${classId}`;
  }

  if (shortWriteId) {
    return `work/short-writes/class/${classId}`;
  }

  return `work/annotations/class/${classId}`;
}

export function useHasActivities() {
  const { quizId, exitTicketId, shortWriteId } = useContext(StoryFilterContext);

  return !!quizId || !!exitTicketId || !!shortWriteId;
}
